import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { ResponseQuery } from "../api/index.js";
import {
  fetchCaptureProgress,
  fetchResponseDetail,
  fetchResponses,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/** Stored answers, and how far today's capture has got. */

const FIRST_PAGE: string | undefined = undefined;

export function useResponses(workspaceId: string, search: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.responses(workspaceId, search),
    queryFn: async ({ pageParam }) => {
      const query: ResponseQuery = Object.fromEntries(new URLSearchParams(search));
      return await fetchResponses(workspaceId, {
        ...query,
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
      });
    },
    // Typed by the annotation rather than by an assertion: the first page has
    // no cursor, and `undefined` on its own would narrow the parameter to
    // undefined for every page after it.
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

export function useResponseDetail(workspaceId: string, runId: string | null) {
  return useQuery({
    queryKey: queryKeys.responseDetail(workspaceId, runId ?? ""),
    queryFn: () => fetchResponseDetail(workspaceId, runId ?? ""),
    enabled: runId !== null,
    // Evidence does not change. Once a capture is sealed its answer, sources
    // and fan-out are immutable (AGENTS.md §14.5), so refetching it on every
    // drawer open costs a round trip to learn nothing.
    staleTime: Infinity,
  });
}

const CAPTURE_POLL_MS = 4_000;

/**
 * How far this report's latest batch has got.
 *
 * Asked every 4s while anything is pending. When nothing is, it stops — unless
 * the caller sets `idlePollMs`. The rail does, at a minute: the daily run starts
 * on the server's clock, so a quiet report has to be asked again to learn that a
 * batch has begun.
 */
export function useCaptureProgress(
  workspaceId: string,
  reportId: string,
  {
    enabled = true,
    idlePollMs,
  }: { readonly enabled?: boolean; readonly idlePollMs?: number } = {},
) {
  return useQuery({
    queryKey: queryKeys.captureProgress(workspaceId, reportId),
    queryFn: () => fetchCaptureProgress(workspaceId, reportId),
    enabled,
    refetchInterval: (query) => {
      const pending = query.state.data?.pending ?? 0;
      return pending > 0 ? CAPTURE_POLL_MS : (idlePollMs ?? false);
    },
  });
}
